import { FormEvent, useEffect } from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Field, ErrorSummary } from "@/components/ui/form-field"
import { track } from "@/lib/analytics"
import type { PageProps } from "@/types/inertia"

// Podcast discoverability audit form. Deliberately two fields only — feed
// URL + email. The feed itself carries the show name, and every extra field
// costs conversions. Mirrors SeoAuditForm's honeypot + UTM/referrer capture
// pattern; both POST and redirect to the token-keyed results page.
export function PodcastAuditForm({
  source,
  ctaLabel = "Run my free audit",
}: {
  source: string
  ctaLabel?: string
}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const form = useForm({
    input_url: "",
    email: "",
    nickname: "",
    referrer: "",
    utm_source: "",
  })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    form.setData((data) => ({
      ...data,
      referrer: document.referrer || "",
      utm_source: params.get("utm_source") || "",
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submit = (e: FormEvent) => {
    e.preventDefault()
    track("Podcast Audit Requested")
    form.post("/podcast-audit")
  }

  // Ids are suffixed so the form can appear more than once on a page without
  // colliding; the summary's links have to follow the same scheme.
  const fieldId = (key: string) => `${key}-${source}`

  return (
    <>
      <ErrorSummary errors={errors} subject="audit request" fieldId={fieldId} />

      <form onSubmit={submit} className="space-y-5" noValidate>
        <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden">
          <label htmlFor={fieldId("nickname")}>Leave this field empty</label>
          <input
            id={fieldId("nickname")}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.data.nickname}
            onChange={(e) => form.setData("nickname", e.target.value)}
          />
        </div>

        <Field label="Your podcast" htmlFor={fieldId("input_url")} error={errors.input_url}>
          <Input
            id={fieldId("input_url")}
            placeholder="Your RSS feed URL (or Apple Podcasts link)"
            inputMode="url"
            value={form.data.input_url}
            onChange={(e) => form.setData("input_url", e.target.value)}
          />
        </Field>
        <Field label="Email" htmlFor={fieldId("email")} error={errors.email}>
          <Input
            id={fieldId("email")}
            type="email"
            autoComplete="email"
            value={form.data.email}
            onChange={(e) => form.setData("email", e.target.value)}
          />
        </Field>
        <Button type="submit" size="lg" disabled={form.processing}>
          {form.processing ? "Starting…" : ctaLabel}
        </Button>
      </form>
    </>
  )
}
